import { useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";

import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";

const THEME_META_COLORS = {
  light: "#f9f8f6",
  dark: "#111917",
} as const;

interface ThemeToggleProps {
  variant?: "floating" | "menu" | "panel";
  className?: string;
}

export function ThemeToggle({ variant = "floating", className }: ThemeToggleProps) {
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme, setTheme } = useTheme();
  const { t } = useLanguage();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!resolvedTheme) {
      return;
    }

    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        "content",
        resolvedTheme === "dark" ? THEME_META_COLORS.dark : THEME_META_COLORS.light,
      );
    }
  }, [resolvedTheme]);

  if (!mounted) {
    return null;
  }

  const isDark = resolvedTheme === "dark";
  const label = isDark ? t("Tema claro") : t("Tema escuro");
  const icon = isDark ? <Sun size={18} /> : <Moon size={18} />;

  return (
    <button
      type="button"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      className={cn(
        variant === "floating"
          ? "fixed bottom-4 right-4 z-[60] inline-flex items-center gap-2 rounded-full border border-clinic-border bg-clinic-surface/90 px-4 py-3 text-sm font-medium text-clinic-text shadow-lg backdrop-blur-md transition hover:bg-clinic-surface-hover"
          : variant === "panel"
            ? "inline-flex h-12 items-center gap-2 rounded-full border border-clinic-border bg-clinic-surface/90 px-4 py-3 text-sm font-medium text-clinic-text shadow-lg backdrop-blur-md transition hover:bg-clinic-surface-hover"
          : "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-clinic-text transition-colors hover:bg-accent hover:text-accent-foreground",
        className,
      )}
      aria-label={isDark ? t("Ativar tema claro") : t("Ativar tema escuro")}
      title={isDark ? t("Ativar tema claro") : t("Ativar tema escuro")}
      data-testid="theme-toggle"
    >
      {icon}
      <span className={variant !== "menu" ? "hidden sm:inline" : undefined}>{label}</span>
    </button>
  );
}
